import React, {useCallback, useState} from 'react';
import {Button, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {AutomationRuleCondition} from '../AutomationRuleCondition';
import {SelectControl} from '../AutomationRuleSelect/SelectControl';
import {IAlertConditionWithID} from '../../../../actions/proto';
import {AutomationChoose} from '../../../../reducer/model';
import {texts} from '../../../../texts';
import {AlertFormFields, RuleType} from '../../../../enums';
import styles from './stlyes.module.scss';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isValidCondition} from './utils';
import getUuid4 from 'uuid/v4';

type Props = {
    addNewCondition: (condition: IAlertConditionWithID) => void;
    removeCondition: (id: string) => void;
    conditions: IAlertConditionWithID[];
    triggerField?: number;
    onChangeCondition: (id: string, data: Partial<IAlertConditionWithID>) => void;
    ifOptions: Array<AutomationChoose>;
    ofOptions: Array<AutomationChoose>;
    autofillFieldOptions: Array<AutomationChoose>;
    isOptions: Array<AutomationChoose>;
    handleUpdateField: (value: Partial<priority.netitems.form.IAlertForm>) => void;
    session: string;
    ruleType: RuleType;
    specialCondition?: React.ReactElement;
};

export const AutomationRuleConditionList = (props: Props) => {
    const {
        conditions,
        ifOptions,
        ofOptions,
        autofillFieldOptions,
        isOptions,
        triggerField: defaultTriggerField,
        session,
        ruleType,
        handleUpdateField,
        addNewCondition,
        removeCondition,
        onChangeCondition,
        specialCondition
    } = props;
    const isAutofillRule = ruleType === RuleType.autofill;
    const isBPM = ruleType === RuleType.bpm;

    const [autofillTriggerField, setAutofillTriggerField] = useState(defaultTriggerField);

    const handleAutofillSource = useCallback(
        (option: AutomationChoose) => {
            const value = Number(option.value);

            setAutofillTriggerField(value);
            handleUpdateField({[AlertFormFields.UpdateField]: Number(option.value)});
        },
        [handleUpdateField]
    );

    const createEmptyCondition = useCallback(() => {
        addNewCondition({
            id: getUuid4(),
            CurrentValue: ifOptions[0]?.value as number
        });
    }, [addNewCondition, ifOptions]);

    const isValid = isValidCondition(conditions);

    return (
        <div className={styles.component}>
            {specialCondition}
            {isAutofillRule && (
                <div className={styles.triggerRow}>
                    <Text
                        tag="label"
                        textColor="primary-700"
                        skin="paragraph-2"
                        componentClasses="pui-label-required"
                    >
                        {texts.autofill_source_field}
                    </Text>

                    <SelectControl
                        onChange={handleAutofillSource}
                        options={autofillFieldOptions}
                        defaultValue={autofillTriggerField}
                        name={AlertFormFields.UpdateField}
                    />
                </div>
            )}

            {conditions.map(condition => {
                return (
                    <AutomationRuleCondition
                        session={session}
                        ifOptions={ifOptions}
                        isOptions={isOptions}
                        ofOptions={ofOptions}
                        isOptional={isAutofillRule}
                        key={condition.id}
                        removeCondition={removeCondition}
                        condition={condition}
                        onChangeCondition={onChangeCondition}
                        isBPM={isBPM}
                    />
                );
            })}

            <Button
                disabled={!isValid}
                componentClasses={styles.addBtn}
                width="contentWidth"
                skin="secondary"
                size="small"
                prefixIcon={PriorityIcon.addSmall}
                onClick={createEmptyCondition}
            >
                {texts.add_new_condition_label}
            </Button>
        </div>
    );
};
